// eslint-disable-next-line no-unused-vars
import React, { memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AddToWishlistModalComponent from 'Components/Wishlists/AddToWishlistModal';
import CreateOffer from 'Containers/Wishlists/AddToModal/CreateOffer';
import CreateWishlist from 'Containers/Wishlists/AddToModal/CreateWishlist';
import SelectDestination from 'Containers/Wishlists/AddToModal/SelectDestination';
import ModalManager from 'Containers/ModalManager/ModalManager';
import useModals from 'Hooks/useModals';
import useAnalytics from 'Hooks/useAnalytics';
import useRouter from 'Hooks/useRouter';
import useUser from 'Hooks/useUser';
import usePermissions from 'Hooks/usePermissions';
import { PERMISSION } from 'Constants';

const getAnalyticsMode = mode => ({
  createOffer: 'create_offer',
  createWishlist: 'create_wishlist'
})[mode] || mode;

const AddToWishlistModal = memo(() => {
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const { push } = useRouter();
  const { data: { isImpersonated } = {} } = useUser();
  const { can } = usePermissions();
  const canAccessOffers = !isImpersonated && can(PERMISSION.ACCESS_ACCOUNT_FEATURES);
  const modalContext = useModals();
  const { key, state } = modalContext.modalState.ADD_TO_WISHLIST;
  const {
    wishlist,
    action,
    product,
    open,
    mode: requestedMode
  } = state;
  const initialMode = !canAccessOffers && requestedMode === 'createOffer'
    ? 'select'
    : requestedMode || 'select';
  const [mode, setMode] = useState(initialMode);
  const [destinationType, setDestinationType] = useState(
    canAccessOffers ? 'offer' : 'wishlist'
  );
  const trackedOpen = useRef(false);

  useEffect(() => {
    if (open) {
      setMode(initialMode);
      if (!trackedOpen.current) {
        trackedOpen.current = true;
        analytics.track('add_to_destination_modal_opened', {
          mode: getAnalyticsMode(initialMode),
          action: action || 'add',
          product_id: product?.productId || product?.id,
          source_wishlist_id: wishlist?.id || null
        });
      }
    } else {
      trackedOpen.current = false;
    }
  }, [
    action,
    analytics,
    initialMode,
    open,
    product?.id,
    product?.productId,
    wishlist?.id
  ]);

  const handleClose = reason => {
    const modalType = mode === 'select'
      ? `add_to_${ destinationType }`
      : getAnalyticsMode(mode);
    modalContext.closeModal(key, {
      type: modalType,
      reason
    });
    setTimeout(() => {
      modalContext.resetModal(key);
      setMode('select');
    }, 250);
  };

  const handleComplete = offer => {
    handleClose('completed');
    if (offer?.id) {
      push(`/offers/private/${ offer.id }/editor`);
    }
  };

  const titles = {
    select: t('offers.addProductTo'),
    createOffer: t('offers.createOffer'),
    createWishlist: t('offers.createNewList')
  };

  const contentProps = {
    product: product || {},
    sourceWishlist: wishlist,
    action,
    open,
    onComplete: handleComplete,
    onCancel: () => handleClose('cancelled')
  };

  return (
    <ModalManager open={ open }>
      <AddToWishlistModalComponent
        open={ open }
        title={ titles[mode] }
        onClose={ () => handleClose('closed') }
      >
        { mode === 'createOffer' && canAccessOffers && <CreateOffer { ...contentProps } /> }
        { mode === 'createWishlist' && <CreateWishlist { ...contentProps } /> }
        { mode === 'select' && (
          <SelectDestination
            { ...contentProps }
            onDestinationTypeChange={ setDestinationType }
            onModeChange={ nextMode => {
              if (!canAccessOffers && nextMode === 'createOffer') return;
              analytics.track('add_to_destination_modal_mode_changed', {
                previous_mode: getAnalyticsMode(mode),
                mode: getAnalyticsMode(nextMode)
              });
              setMode(nextMode);
            } }
          />
        ) }
      </AddToWishlistModalComponent>
    </ModalManager>
  );
});

AddToWishlistModal.displayName = 'AddToWishlistModalContainer';

export default AddToWishlistModal;
